import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  Alert,
  IconButton,
  Grid,
  CircularProgress,
  Toolbar,
  Container,
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  Tooltip,
  Divider,
  Select,
  MenuItem,
} from '@mui/material';

import { useQuery, useMutation, DocumentNode } from '@apollo/client';
import i18next from 'i18next';

import SimpleTable from 'components/table/SimpleTable';
import { VIEW_BOOKING_QUERY, ASSIGN_BOOKING_MUTATION, LIST_BOOKING_ASSIGNABLE_USERS_QUERY } from './gql';
import { SUDO_CLIENT_MUTATION } from '../clients/gql';
import TasksList from 'components/TasksList';
import BookingCommentsSection from '../../components/comments/BookingCommentsSection';
import { userSelector } from 'helper/security';

import { OpenInNew, Login as LoginIcon, Business as BusinessIcon, Person as PersonIcon, ContentCopy as ContentCopyIcon, Edit as EditIcon, Close as CloseIcon } from '@mui/icons-material';
import { EOfferStatus, ViewBookingQuery, EHLBookingStatus, EOfferVersionStatus, EOfferVersionType } from '__generated__/graphql';

interface BookingProps {
  id: number;
}
interface BookingFormProps {
  data: NonNullable<ViewBookingQuery['viewBooking']>;
}

// Status category constants for offer version status
const APPROVED_STATUSES = [EOfferVersionStatus.APPROVED, EOfferVersionStatus.IV_APPROVED];

const PENDING_STATUSES = [
  EOfferVersionStatus.WAITFORCLIENTAPPROVAL,
  EOfferVersionStatus.WAITFORHOTELAPPROVAL,
  EOfferVersionStatus.WAITFORSEMAPPROVAL,
  EOfferVersionStatus.WAITFORHOTELRESERVATION,
  EOfferVersionStatus.ON_HOLD,
  EOfferVersionStatus.CLIENTVERIFICATION,
  EOfferVersionStatus.CR_EDITING,
  EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL,
  EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL,
];

const PENDING_INVOICE_STATUSES = [
  EOfferVersionStatus.CIV_WAITFORHOTEL,
  EOfferVersionStatus.IV_WAITFORCLIENTAPPROVAL,
  EOfferVersionStatus.IV_WAITFORHOTEL,
  EOfferVersionStatus.IV_WAITFORSEMAPPROVAL,
  EOfferVersionStatus.IV_GENERATED,
];

const REJECTED_STATUSES = [
  EOfferVersionStatus.REJECTED_CLIENT,
  EOfferVersionStatus.REJECTED_HOTEL,
  EOfferVersionStatus.REJECTED_HOTEL_WITHDRAWN,
  EOfferVersionStatus.CANCELLED_CLIENT,
  EOfferVersionStatus.CANCELLED_CLIENT_PENDING_HOTEL,
  EOfferVersionStatus.DELETED,
  EOfferVersionStatus.CR_OVERWRITTEN,
  EOfferVersionStatus.HOTEL_OVERWRITTEN,
];

// Helper function to get booking status color
function getStatusColor(status: EHLBookingStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" {
  switch (status) {
    case EHLBookingStatus.PENDING:
      return 'info';
    case EHLBookingStatus.APPROVED:
      return 'primary';
    case EHLBookingStatus.DELETED:
    case EHLBookingStatus.CANCELLED:
      return 'error';
    default:
      return 'default';
  }
}

// Helper function to get offer version status color
function getOfferVersionStatusColor(status: EOfferVersionStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" {
  if (APPROVED_STATUSES.includes(status)) {
    return 'success';
  } else if (PENDING_STATUSES.includes(status) || PENDING_INVOICE_STATUSES.includes(status)) {
    return 'warning';
  } else if (REJECTED_STATUSES.includes(status)) {
    return 'error';
  }
  return 'default';
}

/**
 * Filters offers based on booking status: after PENDING, only approved/active/invoice/closed offers are shown.
 */
function getRelevantOffers(
  offers: Array<{
    id: number;
    status: EOfferStatus;
    latestVersion?: { id: number; status: EOfferVersionStatus } | null;
    editingVersion?: { id: number; status: EOfferVersionStatus } | null;
    versions: Array<{ id: number; status: EOfferVersionStatus; type: EOfferVersionType }>;
    hotel: { id: number; name: string };
  }>,
  bookingStatus: EHLBookingStatus
) {
  // After PENDING status, only show approved/active/invoice/closed offers
  if (bookingStatus !== EHLBookingStatus.PENDING) {
    return offers.filter(offer =>
      offer.status === EOfferStatus.APPROVED ||
      offer.status === EOfferStatus.ACTIVE ||
      offer.status === EOfferStatus.INVOICE ||
      offer.status === EOfferStatus.CLOSED
    );
  }
  return offers;
}

/**
 * Main booking detail form component.
 * Displays booking information, associated offers, tasks, comments, and allows assignment/login-as-client.
 */
function BookingForm(props: BookingFormProps) {
  const user = userSelector();
  const [sudoClientMutation] = useMutation(SUDO_CLIENT_MUTATION);
  const [assignBookingMutation, { loading: assignLoading }] = useMutation(ASSIGN_BOOKING_MUTATION);
  const [bidCopied, setBidCopied] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);

  const { data: assignableUsersData } = useQuery(
    LIST_BOOKING_ASSIGNABLE_USERS_QUERY as unknown as DocumentNode,
    { skip: !user?.isSeminargo },
  );
  type AssignableUser = { id: number; name: string; isLocked?: boolean | null };
  const assignableUsers = ((assignableUsersData as any)?.listTaskUsers ?? [] as AssignableUser[]).filter(
    (u: AssignableUser): boolean => !u.isLocked,
  ) as AssignableUser[];

  const handleAssignUser = async (userId: number) => {
    try {
      await assignBookingMutation({ variables: { bookingId: props.data.id, userId } });
      setShowAssigneeDropdown(false);
    } catch (e: any) {
      console.error('Failed to assign booking:', e);
    }
  };

  const handleLoginAsClient = async () => {
    if (!props.data.publicClient?.id) return;

    try {
      const res = await sudoClientMutation({ variables: { publicClientId: props.data.publicClient.id } });
      const href = res.data?.sudoClient?.href;
      if (href) {
        const w = window.open(
          `${href}&redirect=${encodeURIComponent(`/profile/offers#${props.data.refCode}`)}`,
          '_blank',
          'noopener,noreferrer'
        );
        if (w) w.opener = null;
      }
    } catch (e) {
      console.error('Failed to sudo as client:', e);
    }
  };

  const handleCopyBid = async () => {
    try {
      await navigator.clipboard.writeText(props.data.refCode);
      setBidCopied(true);
      setTimeout(() => setBidCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy BID:', e);
    }
  };

  const relevantOffers = getRelevantOffers(props.data.offers, props.data.status);
  const displayBookingStatus =
    props.data.status === EHLBookingStatus.APPROVED &&
    relevantOffers.some(
      offer =>
        offer.status === EOfferStatus.APPROVED &&
        offer.versions?.some(
          v =>
            v.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL ||
            v.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL
        )
    )
      ? EHLBookingStatus.PENDING
      : props.data.status;

  return (
    <>
      <Helmet>
        <title>
          {i18next.t('booking-page-header')} / {props.data.title}
        </title>
      </Helmet>

      <Grid container spacing={3}>
        {/* General Information Section */}
        <Grid item xs={12}>
          <Paper elevation={2}>
            <Box p={3}>
              <Grid container spacing={2}>
                {/* Left Column: Status, BID, Title */}
                <Grid item xs={12} md={6}>
                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary">
                      {i18next.t('booking-info-status')}
                    </Typography>
                    <Box mt={0.5}>
                      <Chip
                        label={i18next.t(`enums-EHLBookingStatus-${displayBookingStatus}`)}
                        color={getStatusColor(displayBookingStatus)}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary">
                      {i18next.t('booking-info-bid')}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight="medium">
                        {props.data.refCode}
                      </Typography>
                      <Tooltip title={bidCopied ? i18next.t('booking-info-bid-copied') : i18next.t('booking-info-bid-copy')}>
                        <IconButton
                          size="small"
                          onClick={handleCopyBid}
                          color={bidCopied ? 'success' : 'default'}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {i18next.t('booking-info-title')}
                    </Typography>
                    <Typography variant="h6">
                      {props.data.title}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary">
                      {i18next.t('booking-info-assignee')}
                    </Typography>

                    {/* State A: no assignee, seminargo user — show "Assign Employee" CTA */}
                    {user?.isSeminargo && !props.data.responsibleUser && !showAssigneeDropdown && (
                      <Box mt={0.5}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowAssigneeDropdown(true)}
                          disabled={assignLoading}
                        >
                          {i18next.t('booking-info-assign-employee')}
                        </Button>
                      </Box>
                    )}

                    {/* State C: assignee exists, seminargo user — show name as link + edit icon */}
                    {user?.isSeminargo && props.data.responsibleUser && !showAssigneeDropdown && (
                      <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                        <Link
                          to={`/settings/users/${props.data.responsibleUser.id}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <Typography variant="body1" color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                            {props.data.responsibleUser.name}
                          </Typography>
                        </Link>
                        <Tooltip title={i18next.t('booking-info-change-assignee')}>
                          <IconButton
                            size="small"
                            onClick={() => setShowAssigneeDropdown(true)}
                            disabled={assignLoading}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}

                    {/* State B: dropdown open — pick an employee */}
                    {user?.isSeminargo && showAssigneeDropdown && (
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Select
                          size="small"
                          displayEmpty
                          value=""
                          onChange={e => handleAssignUser(Number(e.target.value))}
                          disabled={assignLoading}
                          sx={{ minWidth: 200 }}
                        >
                          <MenuItem value="" disabled>
                            {i18next.t('booking-info-select-employee')}
                          </MenuItem>
                          {assignableUsers.map(u => (
                            <MenuItem key={u.id} value={u.id}>
                              {u.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <Tooltip title={i18next.t('booking-info-change-assignee')}>
                          <IconButton size="small" onClick={() => setShowAssigneeDropdown(false)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}

                    {/* Read-only view for non-seminargo users */}
                    {!user?.isSeminargo && (
                      <Typography variant="body1" mt={0.5}>
                        {props.data.responsibleUser
                          ? props.data.responsibleUser.name
                          : i18next.t('booking-info-assignee-unassigned')}
                      </Typography>
                    )}
                  </Box>

                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary" display="flex" alignItems="center" gap={0.5}>
                      <BusinessIcon fontSize="small" />
                      {i18next.t('booking-info-company')}
                    </Typography>
                    <Box mt={0.5}>
                      {props.data.publicCompany ? (
                        <Link to={`/clients/companies/${props.data.publicCompany.id}`} style={{ textDecoration: 'none' }}>
                          <Typography variant="body1" color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                            {props.data.publicCompany.name}
                          </Typography>
                        </Link>
                      ) : (
                        <Typography variant="body1" color="textSecondary">-</Typography>
                      )}
                    </Box>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary" display="flex" alignItems="center" gap={0.5}>
                      <PersonIcon fontSize="small" />
                      {i18next.t('booking-info-contact')}
                    </Typography>
                    <Box mt={0.5}>
                      {props.data.publicClient ? (
                        <Link to={`/clients/clients/${props.data.publicClient.id}`} style={{ textDecoration: 'none' }}>
                          <Typography variant="body1" color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                            {props.data.publicClient.firstname} {props.data.publicClient.lastname}
                          </Typography>
                        </Link>
                      ) : (
                        <Typography variant="body1" color="textSecondary">-</Typography>
                      )}
                    </Box>
                  </Box>

                  {user?.isSeminargo && props.data.publicClient?.id && (
                    <Box>
                      <Tooltip title={i18next.t('booking-info-login-as-client')}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<LoginIcon />}
                          onClick={handleLoginAsClient}
                        >
                          {i18next.t('booking-info-login-as-client')}
                        </Button>
                      </Tooltip>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Hotel Overview Section */}
        <Grid item xs={12}>
          <Paper elevation={2}>
            <Box p={3}>
              <Typography variant="h6" gutterBottom>
                {i18next.t('booking-hotels-section-title')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <SimpleTable
                rows={relevantOffers.map(offer => {
                  // Pick the best version from all versions (bypasses latestVersion resolver which can return stale data)
                  const RELEVANT_TYPES = [EOfferVersionType.OFFER, EOfferVersionType.RESERVATION_CONFIRMATION, EOfferVersionType.INVOICE];
                  const bestVersion = offer.versions
                    ?.filter(v => RELEVANT_TYPES.includes(v.type))
                    ?.[0] ?? null; // versions are already sorted by id desc from server
                  const crVersion =
                    offer.status === EOfferStatus.APPROVED
                      ? offer.versions?.find(
                          v =>
                            v.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL ||
                            v.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL
                        )
                      : null;
                  const offerVersion = crVersion ?? bestVersion ?? offer.editingVersion ?? offer.latestVersion;
                  return [
                    <Link to={`/settings/hotels/${offer.hotel.id}`} style={{ textDecoration: 'none' }}>
                      <Typography color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                        {offer.hotel.name}
                      </Typography>
                    </Link>,
                    offerVersion ? (
                      <Chip
                        label={i18next.t(
                          offerVersion.status === 'CR_WAITFORHOTELAPPROVAL' && offer.status === 'APPROVED'
                            ? 'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION'
                            : offerVersion.status === 'CR_WAITFORCLIENTAPPROVAL' && offer.status === 'APPROVED'
                            ? 'enums-EOfferVersionStatus-CR_WAITFORCLIENTAPPROVAL-RESERVATION'
                            : `enums-EOfferVersionStatus-${offerVersion.status}`
                        )}
                        size="small"
                        color={getOfferVersionStatusColor(offerVersion.status)}
                      />
                    ) : (
                      <Chip
                        label={i18next.t(`enums-EOfferStatus-${offer.status}`)}
                        size="small"
                        color="default"
                      />
                    ),
                    <IconButton
                      component={Link}
                      to={offer.status === EOfferStatus.INVOICE ? `/invoices/${offer.id}` : `/offers/${offer.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                    >
                      <OpenInNew />
                    </IconButton>,
                  ];
                })}
                headers={[
                  i18next.t('booking-offers-header-hotel'),
                  i18next.t('booking-offers-header-status'),
                  ''
                ]}
              />
            </Box>
          </Paper>
        </Grid>

        {/* ToDos Section */}
        <Grid item xs={12}>
          <Paper elevation={2}>
            <Box p={3}>
              <Typography variant="h6" gutterBottom>
                {i18next.t('booking-todos-section-title')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TasksList
                mode="booking"
                bookingId={props.data.id}
                bookingRefCode={props.data.refCode}
                bookingTitle={props.data.title}
                publicClientId={props.data.publicClient?.id}
                publicCompanyId={props.data.publicCompany?.id}
                showHotelColumn
              />
            </Box>
          </Paper>
        </Grid>

        {/* Booking Comments Section */}
        <Grid item xs={12}>
          <Paper elevation={2}>
            <Box p={3}>
              <BookingCommentsSection
                bookingId={props.data.id}
                companyId={props.data.publicCompany?.id || null}
                clientUserId={props.data.publicClient?.id || null}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}

/**
 * Booking detail page wrapper that loads booking data.
 * Uses network-only fetch policy to ensure fresh data.
 */
export default function Booking(props: BookingProps) {
  const bookingQuery = useQuery(VIEW_BOOKING_QUERY, {
    variables: { id: props.id },
    fetchPolicy: 'network-only',
  });

  if (bookingQuery.loading) return <CircularProgress />;
  else if (bookingQuery.error) return <Alert severity="error">{bookingQuery.error.message}</Alert>;
  else if (!bookingQuery.data?.viewBooking) return <Alert severity="error">{i18next.t('booking-not-found')}</Alert>;
  else
    return (
      <>
        <Toolbar />
        <Container maxWidth="xl">
          <Paper>
            <Box p={2}>
              <BookingForm data={bookingQuery.data!.viewBooking} />
            </Box>
          </Paper>
        </Container>
      </>
    );
}
